import i18n from 'i18next';
import { Dispatch } from 'redux';
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'src/store/api-handler';
import { PackagingDto, PackagingEditDto } from 'src/store/packagings/packaging';
import { alertError, alertSuccess } from '../alerts/alerts.actions';
import { requestBegin, requestEnd } from '../requests/requests.actions';

export enum PackagingTypes {
  FetchAll = 'packagings/FetchAll',
  Add = 'packagings/Add',
  Update = 'packagings/Update',
  Delete = 'packagings/Delete',
  Failure = 'packagings/Failure'
}

export type PackagingAction =
  | { type: PackagingTypes.FetchAll; packagings: PackagingDto[] }
  | { type: PackagingTypes.Add; packaging: PackagingDto }
  | { type: PackagingTypes.Update; packaging: PackagingDto }
  | { type: PackagingTypes.Delete; packaging: PackagingDto }
  | { type: PackagingTypes.Failure; errors: any };

export const actionCreators = {
  fetchPackagings: (packagings: PackagingDto[]): PackagingAction => ({
    type: PackagingTypes.FetchAll,
    packagings
  }),
  addPackaging: (packaging: PackagingDto): PackagingAction => ({
    type: PackagingTypes.Add,
    packaging
  }),
  updatePackaging: (packaging: PackagingDto): PackagingAction => ({
    type: PackagingTypes.Update,
    packaging
  }),
  deletePackaging: (packaging: PackagingDto): PackagingAction => ({
    type: PackagingTypes.Delete,
    packaging
  }),
  packagingRequestFailure: (errors: any): PackagingAction => ({
    type: PackagingTypes.Failure,
    errors
  })
};

export const fetchPackagings = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(PackagingTypes.FetchAll));
    const { ok, data, error } = await getRequest<PackagingDto[]>('/packagings');
    dispatch(requestEnd(PackagingTypes.FetchAll));
    return ok
      ? dispatch(actionCreators.fetchPackagings(data))
      : dispatch(actionCreators.packagingRequestFailure(error));
  };
};

export const createPackaging = (packaging: PackagingEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await postRequest<number>(
      '/packagings',
      packaging
    );

    if (ok) {
      dispatch(alertSuccess(i18n.t('packaging.created')));
      return dispatch(actionCreators.addPackaging({ ...packaging, id: data }));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('create.failed')));
    return dispatch(actionCreators.packagingRequestFailure(error));
  };
};

export const editPackaging = (id: number, packaging: PackagingEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(`/packagings/${id}`, packaging);

    if (ok) {
      dispatch(alertSuccess(i18n.t('packaging.updated')));
      return dispatch(
        actionCreators.updatePackaging(packaging as PackagingDto)
      );
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('save.failed')));
    return dispatch(actionCreators.packagingRequestFailure(error));
  };
};

export const deletePackaging = (id: number, packaging: PackagingDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await deleteRequest(`/packagings/${id}`, packaging);

    if (ok) {
      dispatch(alertSuccess(i18n.t('packaging.deleted')));
      return dispatch(actionCreators.deletePackaging(packaging));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('remove.failed')));
    return dispatch(actionCreators.packagingRequestFailure(error));
  };
};
